<template>
  <section aria-labelledby="efficacy-title" class="efficacy">
    <h2 id="efficacy-title">Efficacité de l'action</h2>

    <p v-if="isLoading" role="status">Chargement de la mesure d'efficacité…</p>

    <v-alert v-else-if="hasError" type="error" text role="alert">
      La mesure d'efficacité n'a pas pu être chargée. Réessayez dans un instant.
    </v-alert>

    <!-- Indisponibilité assumée : sans thématique ciblée ou sans date de démarrage, il n'y a pas
         d'avant / après à mesurer. On le dit, on n'invente pas de résultat. -->
    <v-alert v-else-if="efficacy && !efficacy.hasEfficacy" type="info" text class="mb-0">
      La mesure d'efficacité n'est pas disponible pour cette action : elle suppose une thématique de diagnostic ciblée
      et une date de démarrage.
    </v-alert>

    <template v-else-if="efficacy">
      <p class="subscale">
        Thématique mesurée :
        <strong>{{ efficacy.subscaleName }}</strong>
      </p>

      <v-row>
        <v-col cols="12" md="6">
          <v-card class="cohort-card" :class="cohortClass(efficacy.before)" outlined>
            <v-card-title>Avant l'action</v-card-title>
            <v-card-text>
              <template v-if="efficacy.before.n > 0">
                <p class="cohort-score">{{ formatScore(efficacy.before.meanScore) }}</p>
                <p class="cohort-quadrant">{{ quadrantLabel(efficacy.before.quadrant) }}</p>
                <p class="cohort-n">{{ respondentsLabel(efficacy.before.n) }}</p>
              </template>
              <p v-else class="cohort-empty">Aucune passation avant le démarrage.</p>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="cohort-card" :class="cohortClass(efficacy.after)" outlined>
            <v-card-title>Après l'action</v-card-title>
            <v-card-text>
              <template v-if="efficacy.after.n > 0">
                <p class="cohort-score">{{ formatScore(efficacy.after.meanScore) }}</p>
                <p class="cohort-quadrant">{{ quadrantLabel(efficacy.after.quadrant) }}</p>
                <p class="cohort-n">{{ respondentsLabel(efficacy.after.n) }}</p>
              </template>
              <p v-else class="cohort-empty">Aucune passation depuis le démarrage.</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Évolution : le sens (progression / recul / stabilité) est porté par le texte, la couleur
           ne fait que le redoubler (RGAA 3.1). Nul si une des deux cohortes est vide. -->
      <p v-if="efficacy.delta === null" class="evolution" role="status">
        L'évolution ne peut pas être calculée tant que les deux périodes ne comptent pas de réponse.
      </p>
      <p v-else class="evolution">
        <span class="badge" :class="trendClass">{{ trendLabel }}</span>
        <span class="delta">{{ formatDelta(efficacy.delta) }} point{{ absDelta > 1 ? "s" : "" }}</span>
      </p>

      <p v-if="efficacy.quadrantTransition" class="transition">
        Passage de
        <strong>{{ quadrantLabel(efficacy.quadrantTransition.from) }}</strong>
        à
        <strong>{{ quadrantLabel(efficacy.quadrantTransition.to) }}</strong>
        .
      </p>
    </template>
  </section>
</template>

<script>
import Constants from "@/constants"

// Libellés français des quadrants SWOT — mêmes valeurs que la restitution du diagnostic.
const QUADRANT_LABELS = {
  STRENGTH: "Forces",
  WEAKNESS: "Faiblesses",
  OPPORTUNITY: "Opportunités",
  THREAT: "Menaces",
}

export default {
  name: "ActionEfficacy",
  props: {
    canteenId: {
      type: Number,
      required: true,
    },
    actionId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      efficacy: null,
      status: Constants.LoadingStatus.IDLE,
    }
  },
  computed: {
    isLoading() {
      return this.status === Constants.LoadingStatus.LOADING
    },
    hasError() {
      return this.status === Constants.LoadingStatus.ERROR
    },
    absDelta() {
      return Math.abs((this.efficacy && this.efficacy.delta) || 0)
    },
    trendLabel() {
      const delta = this.efficacy && this.efficacy.delta
      if (delta > 0) return "En progression"
      if (delta < 0) return "En recul"
      return "Stable"
    },
    trendClass() {
      const delta = this.efficacy && this.efficacy.delta
      if (delta > 0) return "badge-green"
      if (delta < 0) return "badge-red"
      return "badge-blue"
    },
  },
  methods: {
    quadrantLabel(quadrant) {
      return QUADRANT_LABELS[quadrant] || quadrant
    },
    // Le quadrant colore la bordure de la cohorte, comme dans la matrice SWOT.
    cohortClass(cohort) {
      return cohort && cohort.quadrant ? cohort.quadrant.toLowerCase() : ""
    },
    formatScore(score) {
      return score === null || score === undefined ? "—" : Number(score).toFixed(1)
    },
    formatDelta(delta) {
      const rounded = Number(delta).toFixed(1)
      return delta > 0 ? `+${rounded}` : rounded
    },
    respondentsLabel(n) {
      return `${n} répondant${n > 1 ? "s" : ""}`
    },
    load() {
      this.status = Constants.LoadingStatus.LOADING
      return this.$store
        .dispatch("fetchActionEfficacy", { canteenId: this.canteenId, actionId: this.actionId })
        .then((response) => {
          this.efficacy = response
          this.status = Constants.LoadingStatus.SUCCESS
        })
        .catch(() => {
          this.status = Constants.LoadingStatus.ERROR
        })
    },
  },
  mounted() {
    this.load()
  },
}
</script>

<style scoped>
.efficacy {
  margin-top: 32px;
}
.subscale {
  margin-bottom: 16px;
}
.cohort-card {
  min-height: 140px;
}
.cohort-score {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 2px;
}
.cohort-quadrant {
  font-weight: 500;
  margin-bottom: 2px;
}
.cohort-n {
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 0;
}
.cohort-empty {
  font-style: italic;
  margin-bottom: 0;
}
.evolution {
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.delta {
  font-weight: 600;
}
.badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}
.badge-green {
  background: #e6f4ea;
  color: #1e7e34;
}
.badge-red {
  background: #fdecea;
  color: #c62828;
}
.badge-blue {
  background: #e7f1fb;
  color: #1565c0;
}
.transition {
  margin-top: 8px;
}
.strength {
  border-left: 5px solid #43a047;
}
.weakness {
  border-left: 5px solid #e53935;
}
.opportunity {
  border-left: 5px solid #1e88e5;
}
.threat {
  border-left: 5px solid #fbc02d;
}
</style>
